import { useEffect, useState } from 'react';
import { Search, Mail, Phone, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new: { label_de: 'Neu', label_en: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_progress: { label_de: 'In Bearbeitung', label_en: 'In Progress', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  done: { label_de: 'Erledigt', label_en: 'Done', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
      {meta.label_de}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtNum(n) {
  if (n == null || n === '') return null;
  try { return Number(n).toLocaleString('de-AT'); } catch { return String(n); }
}

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

export default function AdminCarRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await fetch(`${API_URL}/api/car-requests${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(await res.json());
    } catch (e) {
      toast.error(`Laden fehlgeschlagen: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/car-requests/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setItems(prev => prev.map(x => x.id === id ? updated : x));
      if (selected?.id === id) setSelected(updated);
      toast.success('Status aktualisiert');
    } catch (e) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/car-requests/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(prev => prev.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Gelöscht');
    } catch (e) {
      toast.error(`Fehler: ${e.message}`);
    }
  };

  const summary = (r) => {
    const parts = [];
    if (r.brand) parts.push(r.brand);
    if (r.model) parts.push(r.model);
    return parts.join(' ') || '—';
  };

  const budget = (r) => {
    if (!r.price_from && !r.price_to) return '—';
    const f = r.price_from ? `€${fmtNum(r.price_from)}` : '?';
    const t = r.price_to ? `€${fmtNum(r.price_to)}` : '?';
    return `${f} – ${t}`;
  };

  return (
    <div className="p-6" data-testid="admin-car-requests-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
          <Search size={20} className="text-gold" />
        </div>
        <div>
          <h1 className="font-heading text-2xl">Fahrzeugsuche-Anfragen</h1>
          <p className="text-sm text-[#666666]">Kunden, die uns bitten, ihr Wunschauto zu finden.</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { value: 'all', label: 'Alle' },
          { value: 'new', label: 'Neu' },
          { value: 'in_progress', label: 'In Bearbeitung' },
          { value: 'done', label: 'Erledigt' },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm transition-colors border ${statusFilter === f.value ? 'bg-black text-white border-black' : 'bg-white border-[#E5E5E5] hover:border-black'}`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#666666]">
          <Loader2 size={20} className="animate-spin mr-2" /> Lädt…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-12 text-center text-[#666666]">
          Noch keine Anfragen.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F7] text-left border-b border-[#E5E5E5]">
              <tr>
                <th className="p-4 font-medium">Datum</th>
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Kontakt</th>
                <th className="p-4 font-medium">Fahrzeug</th>
                <th className="p-4 font-medium">Budget</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className="border-b border-[#E5E5E5] hover:bg-[#FAFAF8] cursor-pointer" onClick={() => setSelected(r)} data-testid={`request-row-${r.id}`}>
                  <td className="p-4 text-[#666666]">{formatDate(r.created_at)}</td>
                  <td className="p-4 font-medium">{r.first_name} {r.last_name}</td>
                  <td className="p-4 text-[#666666]">
                    <div className="flex items-center gap-1 text-xs"><Mail size={12} /> {r.email}</div>
                    {r.phone && <div className="flex items-center gap-1 text-xs mt-1"><Phone size={12} /> {r.phone}</div>}
                  </td>
                  <td className="p-4">{summary(r)}</td>
                  <td className="p-4">{budget(r)}</td>
                  <td className="p-4"><StatusBadge status={r.status} /></td>
                  <td className="p-4 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                      className="text-red-500 hover:bg-red-50 p-2 rounded"
                      title="Löschen"
                      data-testid={`delete-${r.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="request-detail">
            <div className="sticky top-0 bg-white border-b border-[#E5E5E5] p-4 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl">{selected.first_name} {selected.last_name}</h2>
                <p className="text-xs text-[#666666]">{formatDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-[#F9F9F7] rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status controls */}
              <div>
                <label className="overline block mb-2">Status</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.keys(STATUS_META).map(s => (
                    <button
                      key={s}
                      disabled={saving || selected.status === s}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`px-4 py-2 rounded-full text-sm border ${selected.status === s ? 'bg-black text-white border-black' : 'bg-white border-[#E5E5E5] hover:border-black'} disabled:opacity-60`}
                      data-testid={`set-status-${s}`}
                    >
                      {STATUS_META[s].label_de}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <section>
                <h3 className="overline mb-3">Kontakt</h3>
                <dl className="grid grid-cols-3 gap-2 text-sm">
                  <dt className="text-[#666666]">Name</dt><dd className="col-span-2 font-medium">{selected.first_name} {selected.last_name}</dd>
                  <dt className="text-[#666666]">E-Mail</dt><dd className="col-span-2"><a className="text-gold hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a></dd>
                  {selected.phone && <><dt className="text-[#666666]">Telefon</dt><dd className="col-span-2"><a className="text-gold hover:underline" href={`tel:${selected.phone}`}>{selected.phone}</a></dd></>}
                  {selected.preferred_contact && <><dt className="text-[#666666]">Bevorzugt</dt><dd className="col-span-2 capitalize">{selected.preferred_contact}</dd></>}
                </dl>
              </section>

              {/* Vehicle */}
              <section>
                <h3 className="overline mb-3">Fahrzeugwünsche</h3>
                <dl className="grid grid-cols-3 gap-2 text-sm">
                  {selected.brand && <><dt className="text-[#666666]">Marke</dt><dd className="col-span-2">{selected.brand}</dd></>}
                  {selected.model && <><dt className="text-[#666666]">Modell</dt><dd className="col-span-2">{selected.model}</dd></>}
                  {(selected.year_from || selected.year_to) && <><dt className="text-[#666666]">Baujahr</dt><dd className="col-span-2">{selected.year_from || '?'} – {selected.year_to || '?'}</dd></>}
                  {(selected.price_from || selected.price_to) && <><dt className="text-[#666666]">Budget</dt><dd className="col-span-2">{budget(selected)}</dd></>}
                  {selected.max_mileage && <><dt className="text-[#666666]">Max. km</dt><dd className="col-span-2">{fmtNum(selected.max_mileage)} km</dd></>}
                  {selected.vehicle_type && <><dt className="text-[#666666]">Typ</dt><dd className="col-span-2">{selected.vehicle_type}</dd></>}
                  {selected.fuel_type && <><dt className="text-[#666666]">Kraftstoff</dt><dd className="col-span-2">{selected.fuel_type}</dd></>}
                  {selected.transmission && <><dt className="text-[#666666]">Getriebe</dt><dd className="col-span-2">{selected.transmission}</dd></>}
                  {selected.drivetrain && <><dt className="text-[#666666]">Antrieb</dt><dd className="col-span-2">{selected.drivetrain}</dd></>}
                  {selected.min_horsepower && <><dt className="text-[#666666]">Mindest-PS</dt><dd className="col-span-2">{selected.min_horsepower}</dd></>}
                  {selected.colors?.length > 0 && <><dt className="text-[#666666]">Farben</dt><dd className="col-span-2">{selected.colors.join(', ')}</dd></>}
                </dl>
              </section>

              {/* Equipment */}
              {selected.equipment?.length > 0 && (
                <section>
                  <h3 className="overline mb-3">Ausstattung</h3>
                  <div className="flex flex-wrap gap-2">
                    {selected.equipment.map(e => (
                      <span key={e} className="px-3 py-1 bg-[#F9F9F7] border border-[#E5E5E5] text-xs rounded-full">{e}</span>
                    ))}
                  </div>
                </section>
              )}

              {/* Wishes */}
              {selected.additional_wishes && (
                <section>
                  <h3 className="overline mb-3">Sonstige Wünsche</h3>
                  <p className="text-sm whitespace-pre-wrap bg-[#F9F9F7] border border-[#E5E5E5] p-4 rounded">{selected.additional_wishes}</p>
                </section>
              )}

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end">
                <button
                  onClick={() => remove(selected.id)}
                  className="text-red-600 hover:bg-red-50 px-4 py-2 rounded flex items-center gap-2 text-sm"
                >
                  <Trash2 size={16} /> Anfrage löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
